import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "@/context/LangContext";

/**
 * Lightweight SEO helper — updates <title>, meta description and canonical for each route.
 * SPA-friendly. Requires no external dependency.
 */
const setMeta = (name, content, attr = "name") => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const SEO = () => {
  const { pathname } = useLocation();
  const { lang } = useLang();

  useEffect(() => {
    const base = window.location.origin;
    const url = base + pathname;

    const seoByPath = {
      "/": {
        fr: { t: "NFC Avis Pro — Cartes NFC pour avis Google | Boostez vos avis en un tap", d: "Cartes NFC premium qui redirigent vos clients vers votre page d'avis Google en un simple tap. Sans app, sans QR. Livraison offerte dès 2 cartes." },
        en: { t: "NFC Avis Pro — NFC Cards for Google Reviews | Boost reviews in one tap", d: "Premium NFC cards that instantly open your Google review page. No app, no QR code. Free shipping on 2+ cards." },
        es: { t: "NFC Avis Pro — Tarjetas NFC para reseñas de Google", d: "Tarjetas NFC premium que abren al instante tu página de reseñas de Google. Sin app, sin QR. Envío gratis desde 2 tarjetas." },
        zh: { t: "NFC Avis Pro — 用于 Google 评价的 NFC 卡", d: "高端 NFC 卡,一触即可打开您的 Google 评价页面。无需应用,无需二维码。购买 2 张起免运费。" },
      },
      "/boutique": {
        fr: { t: "Boutique — Plaques NFC Google | NFC Avis Pro", d: "Achetez votre plaque NFC Google. Pack 1, 2 ou 4 cartes. Français ou anglais. Livraison offerte dès 2 cartes. Paiement Stripe sécurisé." },
        en: { t: "Shop — NFC Google Plates | NFC Avis Pro", d: "Buy your NFC Google plate. Pack of 1, 2 or 4 cards. French or English. Free shipping on 2+. Secure Stripe checkout." },
        es: { t: "Tienda — Placas NFC Google | NFC Avis Pro", d: "Compra tu placa NFC Google. Pack de 1, 2 o 4 tarjetas. Francés o inglés. Envío gratis desde 2. Pago Stripe seguro." },
        zh: { t: "商店 — NFC Google 卡片 | NFC Avis Pro", d: "购买您的 NFC Google 卡片。1、2 或 4 张套装。法语或英语。2 张起免运费。Stripe 安全支付。" },
      },
      "/fonctionnement": {
        fr: { t: "Comment ça marche — Cartes NFC | NFC Avis Pro", d: "Trois secondes, trois étapes : le client approche son téléphone, votre page Google s'ouvre, l'avis est publié. Sans app." },
        en: { t: "How it works — NFC Cards | NFC Avis Pro", d: "Three seconds, three steps: the customer taps their phone, your Google page opens, the review is published. No app." },
        es: { t: "Cómo funciona — Tarjetas NFC | NFC Avis Pro", d: "Tres segundos, tres pasos: el cliente acerca su móvil, se abre tu página de Google, la reseña se publica. Sin app." },
        zh: { t: "工作原理 — NFC 卡 | NFC Avis Pro", d: "三秒钟,三步:顾客靠近手机,您的 Google 页面打开,评价即刻发布。无需应用。" },
      },
      "/faq": {
        fr: { t: "FAQ — Questions fréquentes | NFC Avis Pro", d: "Compatibilité iPhone/Android, livraison, personnalisation, plusieurs entreprises — retrouvez toutes nos réponses." },
        en: { t: "FAQ — Frequently Asked Questions | NFC Avis Pro", d: "iPhone/Android compatibility, shipping, customization, multiple businesses — find all our answers." },
        es: { t: "FAQ — Preguntas frecuentes | NFC Avis Pro", d: "Compatibilidad iPhone/Android, envío, personalización, varias empresas — todas nuestras respuestas." },
        zh: { t: "常见问题 | NFC Avis Pro", d: "iPhone/Android 兼容性、送货、个性化、多家企业 — 找到所有答案。" },
      },
      "/a-propos": {
        fr: { t: "À propos — NFC Avis Pro | Entreprise québécoise", d: "Entreprise québécoise indépendante spécialisée dans les solutions NFC pour PME. Notre mission : faciliter les avis honnêtes." },
        en: { t: "About — NFC Avis Pro | Québec-based company", d: "Independent Québec-based company specialized in NFC solutions for SMBs. Our mission: make honest reviews easier." },
        es: { t: "Nosotros — NFC Avis Pro", d: "Empresa independiente con sede en Quebec, especializada en soluciones NFC para pymes." },
        zh: { t: "关于 — NFC Avis Pro", d: "总部位于魁北克的独立公司,专注于为中小企业提供 NFC 解决方案。" },
      },
      "/contact": {
        fr: { t: "Nous joindre — NFC Avis Pro", d: "Une question ? Contactez notre équipe basée au Québec. Réponse sous 24 h." },
        en: { t: "Contact — NFC Avis Pro", d: "Got a question? Reach our Québec-based team. Reply within 24h." },
        es: { t: "Contacto — NFC Avis Pro", d: "¿Una pregunta? Contacta a nuestro equipo en Quebec. Respuesta en 24h." },
        zh: { t: "联系我们 — NFC Avis Pro", d: "有问题?联系我们位于魁北克的团队。24 小时内回复。" },
      },
      "/panier": {
        fr: { t: "Votre panier — NFC Avis Pro", d: "Vérifiez votre panier. Livraison offerte dès 2 cartes. Paiement Stripe sécurisé." },
        en: { t: "Your cart — NFC Avis Pro", d: "Review your cart. Free shipping on 2+ cards. Secure Stripe payment." },
        es: { t: "Tu carrito — NFC Avis Pro", d: "Revisa tu carrito. Envío gratis desde 2 tarjetas. Pago Stripe seguro." },
        zh: { t: "您的购物车 — NFC Avis Pro", d: "查看您的购物车。购买 2 张起免运费。Stripe 安全支付。" },
      },
      "/suivi": {
        fr: { t: "Suivi de commande — NFC Avis Pro", d: "Suivez votre commande avec votre numéro et courriel." },
        en: { t: "Order tracking — NFC Avis Pro", d: "Track your order with your number and email." },
        es: { t: "Seguimiento — NFC Avis Pro", d: "Sigue tu pedido con tu número y correo." },
        zh: { t: "订单追踪 — NFC Avis Pro", d: "使用订单号和邮箱追踪您的订单。" },
      },
    };

    // Fallback to home if unknown route
    const routeKey = seoByPath[pathname] ? pathname : "/";
    const entry = seoByPath[routeKey][lang] || seoByPath[routeKey].fr;

    document.title = entry.t;
    setMeta("description", entry.d);
    setMeta("og:title", entry.t, "property");
    setMeta("og:description", entry.d, "property");
    setMeta("og:url", url, "property");
    setMeta("twitter:title", entry.t);
    setMeta("twitter:description", entry.d);
    setCanonical(url);

    // Do not index admin, cart, checkout, payment result pages
    const noIndex = pathname.startsWith("/admin") || pathname.startsWith("/paiement") || pathname === "/panier" || pathname === "/checkout";
    setMeta("robots", noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1");
  }, [pathname, lang]);

  return null;
};

export default SEO;
